import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';

/**
 * Drag-and-drop file upload zone. Accepts files via drag-drop OR click.
 * Caller is responsible for the actual upload (passed file → onFile).
 *
 * Props:
 *   label     — visible heading on the zone
 *   hint      — secondary text under label (e.g. accepted types + size)
 *   accept    — MIME pattern forwarded to <input accept>
 *   uploading — bool, swaps the icon for a spinner + disables interaction
 *   onFile    — (file: File) => void; called with the first selected file
 *   id        — unique input id (auto-generated from label if omitted)
 */
export function UploadZone({ label, hint, accept, uploading = false, onFile, id, sx }) {
  const [over, setOver] = useState(false);
  const inputId = id || `upload-${String(label || '').replace(/\s+/g, '-').toLowerCase()}`;

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    if (uploading) return;
    const f = e.dataTransfer.files?.[0];
    if (f) onFile(f);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setOver(false); };
  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (f) onFile(f);
    e.target.value = '';
  };

  return (
    <Box component="label" htmlFor={inputId}
      onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 0.5, py: 2.5, px: 1.5,
        border: '1.5px dashed',
        borderColor: over ? 'primary.main' : 'divider',
        bgcolor: over ? 'action.hover' : 'transparent',
        borderRadius: 1.5, cursor: uploading ? 'wait' : 'pointer',
        transition: 'all 150ms ease',
        opacity: uploading ? 0.7 : 1,
        '&:hover': { borderColor: uploading ? 'divider' : 'primary.main', bgcolor: uploading ? 'transparent' : 'action.hover' },
        ...sx,
      }}>
      {uploading ? <CircularProgress size={20} /> : <UploadIcon sx={{ fontSize: 22, color: 'text.secondary' }} />}
      {label && <Typography variant="body2" fontWeight={600}>{label}</Typography>}
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {hint}
        </Typography>
      )}
      <input id={inputId} type="file" hidden accept={accept} onChange={handleChange} disabled={uploading} />
    </Box>
  );
}

export default UploadZone;
